import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Skeleton,
  UserName,
  cn,
  type UserLifecycle
} from '@bliks-io/ui';

export interface EntityListItem {
  key: string;
  label: string;
  imageUrl?: string;
  initials?: string;
  fallbackIcon?: ReactNode;
  /**
   * When true, the avatar renders with a dashed border and reduced opacity to
   * signal that the entity is an unresolved suggestion (pending or rejected).
   */
  isGhost?: boolean;
  /**
   * When true, the entity is a soft-deleted tombstone: rendered grayed out
   * with a "(deleted)" suffix and sorted to the bottom of the list.
   */
  isDeleted?: boolean;
  /** User lifecycle marker rendered as a muted name suffix (persons only). */
  lifecycle?: UserLifecycle | null;
}

export interface EntityPickerListProps {
  items: EntityListItem[];
  /**
   * Stored entities whose lookup is still loading; rendered as skeleton rows
   * before `items`, where the resolved rows will appear (ghosts come last).
   */
  loadingCount?: number;
}

export function EntityPickerList({ items, loadingCount = 0 }: EntityPickerListProps) {
  const { t } = useTranslation();

  if (items.length === 0 && loadingCount === 0) {
    return (
      <div
        data-testid="entity-picker-list-empty"
        className="mt-2 flex items-center gap-1.5 border-t border-line-subtle pt-2 text-xs text-fg-subtle"
      >
        <Plus className="h-3 w-3" />
        {t('viralRecording.helpersHeading.addHint')}
      </div>
    );
  }

  // Tombstones sink to the bottom; live entries keep their original order.
  const sortedItems = [...items].sort(
    (a, b) => Number(Boolean(a.isDeleted)) - Number(Boolean(b.isDeleted))
  );

  return (
    <ul
      data-testid="entity-picker-list"
      className="mt-2 flex flex-col gap-1.5 border-t border-line-subtle pt-2"
    >
      {Array.from({ length: loadingCount }, (_, i) => (
        <li
          key={`skeleton-${i}`}
          data-testid="entity-picker-list-skeleton-row"
          className="flex items-center gap-2"
        >
          <Skeleton className="h-6 w-6 shrink-0" />
          <Skeleton className="h-4 w-24" />
        </li>
      ))}
      {sortedItems.map((item) => (
        <li
          key={item.key}
          className={cn('flex items-center gap-2 min-w-0', item.isGhost && 'opacity-70')}
        >
          <Avatar
            size="xs"
            className={cn(
              'shrink-0',
              item.isGhost && 'border border-dashed border-muted-foreground/60',
              item.isDeleted && 'grayscale opacity-60'
            )}
          >
            {item.imageUrl && <AvatarImage src={item.imageUrl} alt={item.label} />}
            <AvatarFallback className="text-3xs">
              {item.initials ?? item.fallbackIcon ?? '?'}
            </AvatarFallback>
          </Avatar>
          <UserName
            name={item.label}
            lifecycle={item.lifecycle ?? (item.isDeleted ? 'deleted' : null)}
            className={cn(
              'truncate text-sm',
              item.isDeleted ? 'text-fg-subtle' : 'text-foreground'
            )}
          />
        </li>
      ))}
    </ul>
  );
}
